import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Typography } from '@mui/material';
import { getFirestore, collection, query, where, getDocs } from 'firebase/firestore';

const LeaveDeduction = ({ user, selectedMonth, onSalaryDeduction }) => {
  const [totalLeaveHours, setTotalLeaveHours] = useState(null);
  const [loading, setLoading] = useState(true);
  const [salaryDeduction, setSalaryDeduction] = useState(0);  // State to hold the salary deduction
  const db = getFirestore();

  useEffect(() => {
    console.log("selectedMonth changed to:", selectedMonth); // Log when selectedMonth changes
    const fetchLeaveHours = async () => {
      try {
        if (!user?.id || !selectedMonth) {
          console.error('User ID or selectedMonth is not provided');
          setTotalLeaveHours(null);
          setSalaryDeduction(0);
          setLoading(false);
          return;
        }

        // Parse selectedMonth to extract start and end dates
        const [startDateStr, endDateStr] = selectedMonth.split(' - ');

        // Ensure that both start and end dates include the year (assumes the format includes a year)
        const startDateFormatted = `${startDateStr} ${new Date().getFullYear()}`;
        const endDateFormatted = `${endDateStr} ${new Date().getFullYear()}`;

        const startDate = new Date(startDateFormatted); // Parse to Date
        const endDate = new Date(endDateFormatted); // Parse to Date

        // Ensure end date includes the full day (up to 23:59:59)
        const startMillis = startDate.getTime();
        const endMillis = new Date(endDate).setHours(23, 59, 59, 999);

        // Query Firestore to get leaves matching the user ID
        const leaveRef = collection(db, 'employeeLeave');
        const leaveQuery = query(
            leaveRef,
            where('employeeId', '==', user.id),
            where('status', '==', 'Approved')  
          );
        const querySnapshot = await getDocs(leaveQuery);

        if (!querySnapshot.empty) {
          // Filter leave entries within the selected period and sum total hours
          const totalHours = querySnapshot.docs
            .filter((doc) => {
              const leaveStart = new Date(doc.data().startDate).getTime();
              const leaveEnd = new Date(doc.data().endDate).getTime();
              return (
                (leaveStart >= startMillis && leaveStart <= endMillis) || // Starts within period
                (leaveEnd >= startMillis && leaveEnd <= endMillis) || // Ends within period
                (leaveStart <= startMillis && leaveEnd >= endMillis) // Spans the entire period
              );
            })
            .map((doc) => doc.data()?.totalHours || 0)
            .reduce((acc, hours) => acc + hours, 0);

          setTotalLeaveHours(totalHours > 0 ? totalHours : 0);
        } else {
          setTotalLeaveHours(0); // No matching documents found
        }

        // Calculate salary deduction
        if (user?.hourlyWage && totalLeaveHours !== null) {
          const deduction = user.hourlyWage * totalLeaveHours;
          setSalaryDeduction(deduction);
          onSalaryDeduction(deduction);  // Pass the deduction back to the parent
        } else {
          setSalaryDeduction(0); // If no hourlyWage or totalLeaveHours, set to 0
          onSalaryDeduction(0);  // Pass 0 deduction back to the parent
        }

      } catch (error) {
        console.error('Error fetching leave data:', error);
        setTotalLeaveHours(null);
        setSalaryDeduction(0);
        onSalaryDeduction(0);  // Pass 0 deduction in case of error
      } finally {
        setLoading(false);
      }
    };

    fetchLeaveHours();
  }, [user, selectedMonth, db, totalLeaveHours, onSalaryDeduction]);

  return (
    <div>
      <Typography variant="body1">
        {loading
          ? 'Loading...'
          : totalLeaveHours === null
          ? 'Error loading data'
          : totalLeaveHours === 0
          ? <strong>Leave Hours: 0</strong>
          : <>
          <strong>Leave Hours for the Selected Period:</strong> {totalLeaveHours} Hours
          </>}
      </Typography>
      
      {totalLeaveHours > 0 && !loading && (
        <Typography variant="body1">
          <strong>Salary Deduction (Based on Leave Hours):</strong>
           {salaryDeduction ? ` PKR ${salaryDeduction.toFixed(2)}` : 'PKR 0.00'}
        </Typography>
      )}
    </div>
  );
};

LeaveDeduction.propTypes = {
  user: PropTypes.shape({
    id: PropTypes.string.isRequired,
    hourlyWage: PropTypes.number.isRequired,  // Ensure user has hourlyWage
  }).isRequired,
  selectedMonth: PropTypes.string.isRequired,
  onSalaryDeduction: PropTypes.func.isRequired,  // Add the callback function prop
};

export default LeaveDeduction;
